import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  getSelfServiceListBearersQueryKey,
  useSelfServiceUpdateTraveller,
} from '@/api/trafikportalen';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from '@/hooks/useToast';
import {
  IconTicket,
  IconCreditCard,
  IconUser,
  IconEdit,
  IconCheck,
} from '@/icons';
import { Bearer } from '@/components/Bearer';
import { FavoredBearerAction } from '@/components/FavoredBearerAction';
import { canSetFavoredBearer, isOrderedCardBearer } from '@/helpers/bearers';
import { PendingCardBadge } from '@/components/PendingCardBadge';
import { DialogWindow } from '@/components/Dialog';
import { AddTravelCardDialog } from '@/components/SelfService/AddTravelCardDialog';
import { EditTravelCardDialog } from '@/components/SelfService/EditTravelCardDialog';
import { SelfServiceTicketCard } from '@/components/SelfService/SelfServiceTicketCard';
import { Section } from '@/components/Section';
import Button from '@/components/core/Button';
import Input from '@/components/core/Input';
import { getApiErrorMessage } from '@/helpers/apiError';
import { formatCardNumber } from '@/helpers/formatting';
import { useFavoredBearer } from '@/hooks/useFavoredBearer';
import { isValidEmail } from '@/helpers/validation';
import { blankToUndefined } from '@/helpers/trimData';
import type { TravellerBearerDTO } from '@/api/trafikportalen';
import type { BearerListItem } from '@/hooks/useBearerMapping';
import type { MergedTravellerData } from '@/routes/$tenantId/self-service/_layout/dashboard';
import IconButton from '../core/IconButton';

interface SelfServiceTravellerSectionProps {
  data: MergedTravellerData;
  wardOf?: boolean;
}

export const SelfServiceTravellerSection = ({
  data,
  wardOf,
}: SelfServiceTravellerSectionProps) => {
  const { formatMessage } = useIntl();
  const queryClient = useQueryClient();
  const [addCardOpen, setAddCardOpen] = useState(false);
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [emailValue, setEmailValue] = useState(data.travellerEmail ?? '');

  const updateTraveller = useSelfServiceUpdateTraveller();
  const { setFavoredBearer, getFavoredBearerErrorMessage, isPending } =
    useFavoredBearer({
      travellerId: data.travellerId,
      isSelfService: true,
    });
  const [pendingBearerId, setPendingBearerId] = useState<string | null>(null);
  const isSettingFavored = useRef(false);

  const handleSetFavored = useCallback(
    async (bearerId: string) => {
      if (isSettingFavored.current) return;
      isSettingFavored.current = true;
      setPendingBearerId(bearerId);
      try {
        await setFavoredBearer(bearerId);
      } catch (error) {
        toast.error(
          {
            title: formatMessage({ defaultMessage: 'Ett fel inträffade' }),
            description: getFavoredBearerErrorMessage(error),
          },
          { autoClose: 3000 },
        );
      } finally {
        isSettingFavored.current = false;
        setPendingBearerId(null);
      }
    },
    [formatMessage, getFavoredBearerErrorMessage, setFavoredBearer],
  );

  const trimmedEmail = emailValue.trim();
  const emailError =
    trimmedEmail && !isValidEmail(trimmedEmail)
      ? formatMessage({ defaultMessage: 'Ogiltig e-postadress' })
      : undefined;

  const handleSaveEmail = useCallback(async () => {
    if (trimmedEmail && !isValidEmail(trimmedEmail)) return;

    try {
      await updateTraveller.mutateAsync({
        travellerID: data.travellerId,
        data: {
          bobcat_private_email: blankToUndefined(trimmedEmail),
        },
      });

      await queryClient.invalidateQueries({
        queryKey: getSelfServiceListBearersQueryKey(),
      });

      toast.success(
        {
          title: formatMessage({
            defaultMessage: 'E-postadressen har uppdaterats',
          }),
        },
        { autoClose: 3000 },
      );
      setIsEditingEmail(false);
      setEmailValue(trimmedEmail);
    } catch (error) {
      toast.error(
        {
          title: formatMessage({
            defaultMessage: 'Kunde inte uppdatera e-postadressen',
          }),
          description: getApiErrorMessage(
            error,
            formatMessage({ defaultMessage: 'Ett fel inträffade' }),
          ),
        },
        { autoClose: 3000 },
      );
    }
  }, [
    updateTraveller,
    data.travellerId,
    trimmedEmail,
    queryClient,
    formatMessage,
  ]);

  useEffect(() => {
    if (!isEditingEmail) {
      setEmailValue(data.travellerEmail ?? '');
    }
  }, [data.travellerEmail, isEditingEmail]);

  const handleCancelEmail = useCallback(() => {
    setEmailValue(data.travellerEmail ?? '');
    setIsEditingEmail(false);
  }, [data.travellerEmail]);

  const tickets = data.ticketEntry?.tickets ?? [];
  const { alltix_bearers, private_bearers, favored_bobcat_bearer_id } =
    data.bearerEntry ?? {};

  const existingCardSerials = useMemo(() => {
    const serials: string[] = [];

    for (const bearer of [
      ...(alltix_bearers ?? []),
      ...(private_bearers ?? []),
    ]) {
      if (bearer.serial) {
        serials.push(bearer.serial);
      }
    }

    return serials;
  }, [alltix_bearers, private_bearers]);

  const bearerItems = useMemo<BearerListItem[]>(() => {
    const fromAlltix: BearerListItem[] = (alltix_bearers ?? []).map((b) => ({
      ...b,
      source: 'alltix',
    }));
    const fromPrivate: BearerListItem[] = (private_bearers ?? []).map((b) => ({
      ...b,
      source: 'private',
    }));
    return [...fromAlltix, ...fromPrivate];
  }, [alltix_bearers, private_bearers]);

  return (
    <Section className="gap-10">
      {data.organizationName && (
        <span className="font-medium tracking-wide text-text-800">
          {data.organizationName}
        </span>
      )}
      <div className="flex flex-col">
        {wardOf && (
          <span className="text-sm font-medium uppercase tracking-wide text-text-700">
            <FormattedMessage defaultMessage="Vårdnadshavare för" />
          </span>
        )}
        {data.name ? (
          <div className="flex items-center gap-2">
            <IconUser className="size-5 text-text-800" aria-hidden="true" />
            <h2 className="text-lg font-medium text-text-900">{data.name}</h2>
          </div>
        ) : null}
      </div>

      <div className="flex flex-col gap-1">
        <div className="flex items-center justify-between">
          <label
            htmlFor={`email-${data.travellerId}`}
            className="font-medium text-text-700"
          >
            <FormattedMessage defaultMessage="E-post i appen / Mina sidor" />
          </label>

          <IconButton
            variant="ghost"
            onClick={() => setIsEditingEmail(true)}
            aria-label={formatMessage({
              defaultMessage: 'Redigera e-post i appen / Mina sidor',
            })}
            icon={<IconEdit />}
            disabled={isEditingEmail}
          />
        </div>
        {isEditingEmail ? (
          <div className="flex flex-col gap-2">
            <Input
              id={`email-${data.travellerId}`}
              type="email"
              value={emailValue}
              onChange={(e) => setEmailValue(e.target.value)}
              placeholder={formatMessage({
                defaultMessage: 'hello@example.com',
              })}
              error={emailError}
              helperTextReserveSpace
            />

            <div className="flex w-full justify-end gap-2">
              <Button
                variant="ghost"
                disabled={updateTraveller.isPending}
                onClick={handleCancelEmail}
              >
                <FormattedMessage defaultMessage="Avbryt" />
              </Button>
              <Button
                variant="filled"
                loading={updateTraveller.isPending}
                disabled={!!emailError}
                onClick={handleSaveEmail}
                startIcon={<IconCheck />}
              >
                <FormattedMessage defaultMessage="Spara" />
              </Button>
            </div>
          </div>
        ) : (
          <span className="text-text-800">
            {data.travellerEmail || (
              <i className="text-text-700">
                <FormattedMessage defaultMessage="Ingen e-post angiven" />
              </i>
            )}
          </span>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <h3 className="flex items-center gap-2 font-medium text-text-700">
          <IconTicket className="size-4" aria-hidden="true" />
          <FormattedMessage defaultMessage="Biljetter" />
        </h3>
        {tickets.length > 0 ? (
          <ul className="flex flex-col gap-2">
            {tickets.map((ticket) => (
              <SelfServiceTicketCard
                key={ticket.id}
                ticket={ticket}
                travellerId={data.travellerId}
                bearerItems={bearerItems}
                mostRecentBobcatBearerId={favored_bobcat_bearer_id}
              />
            ))}
          </ul>
        ) : (
          <i className="text-text-700">
            <FormattedMessage defaultMessage="Inga aktiva biljetter" />
          </i>
        )}
      </div>

      <div className="flex flex-col gap-2">
        <h3 className="flex items-center gap-2 font-medium text-text-700">
          <IconCreditCard className="size-4" aria-hidden="true" />
          <FormattedMessage defaultMessage="Biljettbärare" />
        </h3>

        <div className="mb-5 flex flex-col gap-2">
          <b className="mb-3 text-text-800">
            <FormattedMessage defaultMessage="Från resenärens privata konto (hämtat via 'E-post i appen / Mina sidor'):" />
          </b>
          {private_bearers && private_bearers.length > 0 ? (
            <ul className="flex flex-col gap-2">
              {private_bearers.map((bearer) => {
                const isFavored = bearer.id === favored_bobcat_bearer_id;
                return (
                  <li
                    key={bearer.id}
                    className="border-b border-b-background-200 pb-2 last:border-b-0"
                  >
                    <div className="flex flex-wrap items-center gap-2">
                      <Bearer bearer={bearer} />
                      <FavoredBearerAction
                        bearer={bearer}
                        isFavored={isFavored}
                        canSet={canSetFavoredBearer(bearer)}
                        isPending={isPending && pendingBearerId === bearer.id}
                        onSetFavored={handleSetFavored}
                      />
                    </div>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="italic text-text-700">
              <FormattedMessage defaultMessage="Inga privata bärare. Privata kort / Smartphone syns när E-post i appen / Mina sidor är kopplad." />
            </p>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <b className="text-text-800">
            <FormattedMessage defaultMessage="Systemägda resekort:" />
          </b>
          {alltix_bearers && alltix_bearers.length > 0 ? (
            <ul className="flex flex-col gap-2">
              {alltix_bearers.map((bearer) => (
                <AlltixCardRow
                  key={bearer.id}
                  bearer={bearer}
                  travellerId={data.travellerId}
                  isFavored={bearer.id === favored_bobcat_bearer_id}
                  isSettingFavored={isPending && pendingBearerId === bearer.id}
                  onSetFavored={handleSetFavored}
                />
              ))}
            </ul>
          ) : (
            <i className="text-text-700">
              <FormattedMessage defaultMessage="Inga systemägda resekort" />
            </i>
          )}

          <Button
            variant="outline"
            className="ml-auto"
            onClick={() => setAddCardOpen(true)}
          >
            <FormattedMessage defaultMessage="Lägg till systemägt resekort" />
          </Button>
        </div>

        <DialogWindow
          title={formatMessage({
            defaultMessage: 'Lägg till systemägt resekort',
          })}
          content={
            <AddTravelCardDialog
              travellerId={data.travellerId}
              existingCardSerials={existingCardSerials}
              onOpenChange={setAddCardOpen}
            />
          }
          onOpenChange={setAddCardOpen}
          open={addCardOpen}
        />
      </div>
    </Section>
  );
};

interface AlltixCardRowProps {
  bearer: TravellerBearerDTO;
  travellerId: string;
  isFavored: boolean;
  isSettingFavored: boolean;
  onSetFavored: (bearerId: string) => void;
}

const AlltixCardRow = ({
  bearer,
  travellerId,
  isFavored,
  isSettingFavored,
  onSetFavored,
}: AlltixCardRowProps) => {
  const { formatMessage } = useIntl();
  const [editOpen, setEditOpen] = useState(false);
  const serial = bearer.serial?.trim();

  const handleEdit = useCallback(() => {
    setEditOpen(true);
  }, []);

  const cardLabel =
    bearer.name?.trim() ||
    (serial
      ? formatCardNumber(serial)
      : formatMessage({ defaultMessage: 'beställt kort' }));

  return (
    <li className="border-b border-b-background-200 pb-2 last:border-b-0">
      <div className="flex flex-wrap items-center gap-2">
        <Bearer bearer={bearer} />
        {isOrderedCardBearer(bearer) && (
          <PendingCardBadge state="card-ordered" />
        )}
        <div className="ml-auto flex items-center gap-1">
          <FavoredBearerAction
            bearer={bearer}
            isFavored={isFavored}
            canSet={canSetFavoredBearer(bearer)}
            isPending={isSettingFavored}
            onSetFavored={onSetFavored}
          />
          <IconButton
            aria-label={formatMessage(
              { defaultMessage: 'Redigera kort {card}' },
              { card: cardLabel },
            )}
            icon={<IconEdit />}
            variant="ghost"
            onClick={handleEdit}
          />
        </div>
      </div>
      {editOpen && (
        <DialogWindow
          title={formatMessage({ defaultMessage: 'Redigera kort' })}
          content={
            <EditTravelCardDialog
              travellerId={travellerId}
              bearerId={bearer.id}
              serial={serial}
              currentName={bearer.name}
              onOpenChange={setEditOpen}
            />
          }
          onOpenChange={setEditOpen}
          open={editOpen}
        />
      )}
    </li>
  );
};
